// Returns the lowest enabled odds value in a market, or Infinity if none.
export const marketFavoriteOdds = (market) => {
  if (!market?.outcomes?.length) return Infinity;
  const enabled = market.outcomes.filter((o) => o.enabled);
  if (!enabled.length) return Infinity;
  return Math.min(...enabled.map((o) => o.odds));
};

// Returns the lowest-odds enabled outcome in a market, or null.
export const marketFavoriteOutcome = (market) => {
  if (!market?.outcomes?.length) return null;
  const enabled = market.outcomes.filter((o) => o.enabled);
  if (!enabled.length) return null;
  return enabled.reduce((best, o) => (o.odds < best.odds ? o : best));
};

// Hardcoded market display priority. Markets in this list appear FIRST in the
// declared order; any others fall through in their original API order.
export const MARKET_PRIORITY = [
  '534-m-S:pre:markettext:176991', // Winner
  '534-m-S:pre:markettext:341808', // Golden Boot
  '536-m-S:pre:markettext:341834-S:2', // Reach the Final
  // League outrights. Every Winner is listed ahead of every Top Goalscorer so a
  // league always leads with its Winner market.
  '534-m-S:pre:markettext:373116', // Champions League Winner
  '534-m-S:pre:markettext:371100', // Premier League Winner
  '534-m-S:pre:markettext:375314', // Ligue 1 Winner
  '534-m-S:pre:markettext:371730', // LaLiga Winner
  '534-m-S:pre:markettext:375316', // Serie A Winner
  '534-m-S:pre:markettext:370332', // Bundesliga Winner
  '534-m-S:pre:markettext:375192', // DFB Pokal Winner
  '534-m-S:pre:markettext:386076', // Super League Winner
  '534-m-S:pre:markettext:391706', // Europa League Winner
  '534-m-S:pre:markettext:391708', // Conference League Winner
  '534-m-S:pre:markettext:379372', // Eredivisie Winner
  '534-m-S:pre:markettext:384612', // Premier League Top Goalscorer
  '534-m-S:pre:markettext:387432', // Ligue 1 Top Goalscorer
  '534-m-S:pre:markettext:387180', // LaLiga Top Goalscorer
  '534-m-S:pre:markettext:388578' // Bundesliga Top Goalscorer
];

// Hardcoded season (event) display priority. Events in this list appear FIRST
// in the declared order; any others fall through in their original API order.
export const SEASON_PRIORITY = [
  'sr:season:140786', // UEFA Champions League 26/27
  'sr:season:140756', // Premier League 26/27
  'sr:season:140768', // Bundesliga 26/27
  'sr:season:143916', // Süper Lig 26/27
  'sr:season:101177', // World Cup 2026
  'sr:season:131129', // UEFA Champions League 25/26
  'sr:season:142176', // LaLiga 26/27
  'sr:season:140782', // Serie A 26/27
  'sr:season:140780', // Ligue 1 26/27
  'sr:season:140774', // DFB Pokal 26/27
  'sr:season:142776', // UEFA Europa League 26/27
  'sr:season:142778', // UEFA Conference League 26/27
  'sr:season:142628' // Eredivisie 26/27
];

// Stable priority sort for outright events (one event = one season).
export const sortEventsByPriority = (events) => {
  if (!events?.length) return [];
  const priorityIndex = (e) => {
    const i = SEASON_PRIORITY.indexOf(e?.se?.id);
    return i === -1 ? Infinity : i;
  };
  return events
    .map((e, i) => ({ e, i, p: priorityIndex(e) }))
    .sort((a, b) => a.p - b.p || a.i - b.i)
    .map((x) => x.e);
};

// Stable priority sort: priority items first (in declared order), others retain
// their original index so unspecified markets stay in API order.
export const sortMarketsByPriority = (markets) => {
  if (!markets?.length) return [];
  const priorityIndex = (m) => {
    const i = MARKET_PRIORITY.indexOf(m.stringId);
    return i === -1 ? Infinity : i;
  };
  return markets
    .map((m, i) => ({ m, i, p: priorityIndex(m) }))
    .sort((a, b) => a.p - b.p || a.i - b.i)
    .map((x) => x.m);
};

// Top N markets ordered by hardcoded priority (then original API order).
// Returns array of { market, favorite } pairs.
export const selectTopMarkets = (event, n = 3) => {
  if (!event?.markets?.length) return [];
  return sortMarketsByPriority(event.markets)
    .map((market) => ({ market, favorite: marketFavoriteOutcome(market) }))
    .filter((entry) => entry.favorite !== null)
    .slice(0, n);
};

// Top N markets each with top M outcomes (lowest odds first).
// Returns array of { market, outcomes: [...], totalOutcomes }.
export const selectTopMarketsWithOutcomes = (event, marketCount = 2, outcomeCount = 2) => {
  if (!event?.markets?.length) return [];
  return sortMarketsByPriority(event.markets)
    .map((market) => {
      const enabled = (market.outcomes || []).filter((o) => o.enabled);
      const sorted = [...enabled].sort((a, b) => a.odds - b.odds);
      return {
        market,
        outcomes: sorted.slice(0, outcomeCount),
        totalOutcomes: enabled.length
      };
    })
    .filter((entry) => entry.outcomes.length > 0)
    .slice(0, marketCount);
};

// Translated event (season) name by season id, with fallback to raw eventName.
// Lookup table: src/language/<locale>.json -> outrights.events[seasonId].
export const eventDisplayName = (event, t) => {
  if (!event?.se) return '';
  if (typeof t === 'function' && event.se.id) {
    const key = `outrights.events.${event.se.id}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  return event.se.eventName || '';
};

// Translated market label by stringId, with fallback to raw market.name.
// Lookup table: src/language/<locale>.json -> outrights.markets[stringId].
export const marketDisplayName = (market, t) => {
  if (!market) return '';
  if (typeof t === 'function' && market.stringId) {
    const key = `outrights.markets.${market.stringId}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  return market.name || '';
};

// One category chip per market (keyed by stringId so each market gets a unique
// translated label). Falls back to raw market.name when no translation exists.
// Order follows MARKET_PRIORITY so priority chips appear first.
export const marketCategories = (event, t) => {
  if (!event?.markets?.length) return [];
  return sortMarketsByPriority(event.markets).map((m) => ({
    id: m.stringId,
    label: marketDisplayName(m, t),
    count: m.outcomes?.length || 0
  }));
};

// Days left between now and an ISO timestamp; negative if past.
export const daysLeft = (isoStart, now = Date.now()) => {
  if (!isoStart) return null;
  const start = new Date(isoStart).getTime();
  if (Number.isNaN(start)) return null;
  return Math.ceil((start - now) / (1000 * 60 * 60 * 24));
};

// Implied probability percentage from decimal odds, rounded to int.
export const impliedProb = (odds) => {
  if (!odds || odds <= 0) return 0;
  return Math.round(100 / odds);
};

// Format ISO date as short e.g. "Jun 11"
export const formatShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
